import { useState } from 'react';
import { Paperclip } from 'lucide-react';
import type { TicketDocumentos } from '@sime/shared';
import { CertificadosModal } from '@/components/domain/certificados-modal';
import { PdfIconButton } from './pdf-icon-button';
import { cn } from '@/lib/utils';

interface DocumentosBadgeProps {
  ticketId: number;
  correlativo: string;
  documentos: TicketDocumentos;
  size?: 'xs' | 'sm';
  /** Spell out "Certificados (N)". Off for the dense monthly grid cards. */
  labeled?: boolean;
}

/**
 * Document indicators for a ticket row, on every list surface: the
 * monthly/weekly/daily calendars and the Lista tab.
 *
 * The certificate pill is the one that matters to the Codelco coordinator —
 * the certification documents attached to the ticket (`ot_certificado`). It is
 * the same badge, same wording and same click-to-open gesture as the
 * coordinator miniapp's histórico list, reading the same rows by the same rule
 * (not deleted, non-empty S3 key). Certificates were invisible from every list
 * in the platform, so a coordinator who did not open each ticket one by one
 * never knew which ones had documents.
 *
 * The PDF button beside it is a *different* artifact — the ticket's own signed
 * work order — labeled as such so it is never read as a certification. It now
 * renders only when a document actually exists; the old rule was
 * `estadoOt ∈ {EJECUTADO, APROBADO}`, which on legacy INS periods put an icon
 * on 264 tickets that have nothing behind it.
 */
export function DocumentosBadge({
  ticketId,
  correlativo,
  documentos,
  size = 'sm',
  labeled = false,
}: DocumentosBadgeProps) {
  const { pdfEstado, certificadoCount } = documentos;
  const [certsOpen, setCertsOpen] = useState(false);

  if (pdfEstado === null && certificadoCount === 0) return null;

  const openCerts = (e: React.MouseEvent | React.KeyboardEvent) => {
    // The whole row navigates to the ticket detail; opening the certificates
    // must not also trigger that.
    e.stopPropagation();
    setCertsOpen(true);
  };

  return (
    <>
      {certificadoCount > 0 ? (
        <span
          role="button"
          tabIndex={0}
          onClick={openCerts}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openCerts(e); }}
          className={cn(
            'inline-flex cursor-pointer items-center gap-1 rounded-md font-semibold leading-none',
            'border border-blue-200 bg-blue-50 text-blue-700',
            'dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
            'hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors',
            'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-sime-accent',
            size === 'xs' ? 'px-1 py-0.5 text-[10px]' : 'px-2 py-1 text-[12px]',
          )}
          aria-label={`Ver certificados de ${correlativo}`}
          title={`Ver certificados de ${correlativo}`}
        >
          <Paperclip className={size === 'xs' ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'} aria-hidden="true" />
          {labeled ? `Certificados (${String(certificadoCount)})` : certificadoCount}
        </span>
      ) : null}

      {pdfEstado !== null ? (
        <PdfIconButton
          ticketId={ticketId}
          size={size}
          tone={pdfEstado === 'FIRMADO' ? 'firmado' : 'borrador'}
          labeled={labeled}
        />
      ) : null}

      {certsOpen ? (
        <CertificadosModal
          ticketId={ticketId}
          correlativo={correlativo}
          onClose={() => { setCertsOpen(false); }}
        />
      ) : null}
    </>
  );
}
